<template>
  <div class="ivpump-options-content">
    <div data-testid="function-container">
      <label>Function</label>
      <help-tooltip v-if="pumpFunctionError" type="error" aria-label="Pump Function Not Selected Error" data-testid="pump-function-not-selected-error">
        {{ pumpFunctionError.message }}
      </help-tooltip>
      <md-field>
        <md-select
          :value="pumpSettings.pumpFunction"
          placeholder="Select function"
          :disabled="!isEnabled"
          aria-label="Function"
          data-testid="function-select"
          @input="value => updateField('pumpFunction', value)"
        >
          <md-option value="SET_RATE" aria-label="Set Rate" data-testid="set-rate-option">
            Set Rate
          </md-option>
          <md-option value="FORMULARY" aria-label="Formulary Selection" data-testid="formulary-selection-option">
            Formulary Selection
          </md-option>
          <md-option value="CUSTOM" aria-label="Custom Dose" data-testid="custom-dose-option">
            Custom Dose
          </md-option>
        </md-select>
      </md-field>
    </div>

    <div class="formulary-options" data-testid="formulary-options-container">
      <div
        v-if="pumpSettings.pumpFunction !== 'SET_RATE' && needWeight"
      >
        <label>Weight</label>
        <md-field>
          <md-input
            :value="pumpSettings.weight || ''"
            :disabled="!isEnabled"
            aria-label="Weight"
            data-testid="weight-input"
            @input="
              value => updateField('weight', parseInt(value, 10) || 0)"
          />

          <span class="md-suffix">kg</span>
        </md-field>
      </div>
      <span>&nbsp;</span>
      <div
        v-if="pumpSettings.pumpFunction === 'CUSTOM'"
        class="formulary-form-container"
      >
        <formulary-form
          :formulary="pumpSettings.formulary ? pumpSettings.formulary : {}"
          :has-volume-select="false"
          :inline-labels="false"
          :readonly="!isEnabled"
          @update="value => updateField('formulary', value)"
        />
      </div>
      <div v-if="pumpSettings.pumpFunction === 'FORMULARY'">
        <label>Dosing</label>
        <md-field>
          <md-select
            :value="formularyString"
            :disabled="!isEnabled"
            class="formulary-select"
            placeholder="Formulary"
            aria-label="Dosing"
            data-testid="dosing-select"
            @input="value => updateField('formulary', JSON.parse(value))"
          >
            <md-option
              v-for="(formulary, index) in availableFormularies"
              :key="index"
              :value="JSON.stringify(formulary)"
              :disabled="isFormularyDisabled(formulary)"
              :label="
                `${formulary.dose}${dosingTypeLabel(
                  formulary.dosing,
                )} (${volumeLabel(formulary.volume)})`
              "
              :aria-label="
                `${formulary.dose}${dosingTypeLabel(
                  formulary.dosing,
                )} (${volumeLabel(formulary.volume)})`
              "
              data-testid="dosing-option"
            >
              {{ formulary.dose }}{{ dosingTypeLabel(formulary.dosing) }} ({{ volumeLabel(formulary.volume) }})
            </md-option>
          </md-select>
        </md-field>
      </div>
    </div>

    <div
      class="volume-rate-time-options"
      data-testid="volume-rate-time-container"
    >
      <div data-testid="vtbi-container">
        <label>VTBI</label>
        <help-tooltip v-if="pumpVtbiError" type="error" aria-label="VTBI Error" data-testid="vtbi-error">
          {{ pumpVtbiError.message }}
        </help-tooltip>
        <md-field>
          <md-input
            :value="pumpSettings.vtbi || ''"
            :disabled="!isEnabled"
            aria-label="VTBI"
            data-testid="vtbi-input"
            @input="value => updateField('vtbi', parseInt(value, 10) || 0)"
          />

          <span class="md-suffix">mL</span>
        </md-field>
      </div>
      <div data-testid="rate-container">
        <label>Rate</label>
        <help-tooltip v-if="pumpRateError" type="error" aria-label="Rate Error" data-testid="rate-error">
          {{ pumpRateError.message }}
        </help-tooltip>
        <md-field>
          <md-input
            :value="pumpSettings.rate || ''"
            :disabled="!isEnabled || pumpSettings.pumpFunction !== 'SET_RATE'"
            aria-label="Rate"
            data-testid="rate-input"
            @input="value => updateField('rate', parseInt(value, 10) || 0)"
          />

          <span class="md-suffix">mL/hr</span>
        </md-field>
      </div>
      <div data-testid="time-container">
        <label>Time</label>
        <help-tooltip v-if="pumpTimeError" type="error" aria-label="Time Error" data-testid="time-error">
          {{ pumpTimeError.message }}
        </help-tooltip>
        <md-field>
          <md-input
            :value="pumpSettings.hours || ''"
            :disabled="!isEnabled || pumpSettings.pumpFunction === 'FORMULARY'"
            aria-label="Hours"
            data-testid="hours-input"
            @input="value => updateField('hours', parseInt(value, 10) || 0)"
          />

          <span class="md-suffix">hours</span>
        </md-field>
        <md-field>
          <md-input
            :value="pumpSettings.minutes || ''"
            :disabled="!isEnabled || pumpSettings.pumpFunction === 'FORMULARY'"
            aria-label="Minutes"
            data-testid="minutes-input"
            @input="value => updateField('minutes', parseInt(value, 10) || 0)"
          />

          <span class="md-suffix">minutes</span>
        </md-field>
      </div>
    </div>
  </div>
</template>

<script>
import assign from 'lodash/assign';
import omit from 'lodash/omit';

import {
  infusionBagVolumes,
  infusionDosingTypes,
} from '~lib/common/config/medication';
import HelpTooltip from '~lib/common/components/HelpTooltip';

import FormularyForm from '../../FormularyForm';

const nullIfInfinity = val => (val === Infinity ? null : val);

export default {
  name: 'initial-pump-settings',
  components: {
    FormularyForm,
    HelpTooltip,
  },
  props: {
    pumpSettings: {
      type: Object,
      required: true
    },
    isEnabled: {
      type: Boolean,
      required: true,
    },
    concentration: {
      type: Object,
      required: true,
    },
    formularies: {
      type: Array,
      required: true,
    },
    bagVolume: {
      type: String,
      required: true,
    },
    errors: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      isUpdating: false,
    }
  },
  computed: {
    formularyString() {
      const str = this.pumpSettings.formulary
        ? JSON.stringify(omit(this.pumpSettings.formulary, '__typename'))
        : null;
      return str;
    },
    availableFormularies() {
      let available = this.formularies;
      // In case the initial med formulary is not in the list of available
      // formularies, add it so that it will be displayed correctly (this
      // formulary is not linked to the med's formulary)
      const initF = this.pumpSettings.formulary;
      if (initF && !available.find(
        f => f.dose === initF.dose && f.dosing === initF.dosing && f.volume === initF.volume
      )) {
        available.push(this.pumpSettings.formulary);
      }
      // Because formularies dont have ids, we use their stringified
      // values as keys for the select, we need to remove the GraphQL
      // typename first though because it is not sent back by apollo
      return available.map(formulary => omit(formulary, '__typename'));
    },
    needWeight() {
      let dosingType;
      if (
        (this.pumpSettings.pumpFunction === 'FORMULARY' || this.pumpSettings.pumpFunction === 'CUSTOM') &&
        this.pumpSettings.formulary
        ) {
        dosingType = this.pumpSettings.formulary.dosing;
        console.log(dosingType)
      }
      return dosingType && dosingType.includes('_KG');
    },
    pumpFunctionError() {
      return this.errors ? this.errors['pumpFunction'] : null;
    },
    pumpVtbiError() {
      return this.errors ? this.errors['pumpVtbi'] : null;
    },
    pumpRateError() {
      return this.errors ? this.errors['pumpRate'] : null;
    },
    pumpTimeError() {
      return this.errors ? this.errors['pumpTime'] : null;
    }
  },
  methods: {
    dosingTypeLabel(dosingTypeKey) {
      const dosingType = infusionDosingTypes.find(d => d.key === dosingTypeKey);
      return dosingType ? dosingType.label.en : 'N/A';
    },
    volumeLabel(volumeKey) {
      const volume = infusionBagVolumes.find(v => v.key === volumeKey);
      return volume ? volume.label.en : 'N/A';
    },
    computeVolume(volumeKey) {
      const bagVolume = infusionBagVolumes.find(bv => bv.key === volumeKey);
      return bagVolume ? bagVolume.volumeML : -1;
    },
    isFormularyDisabled(formulary) {
      const maxVolume = this.computeVolume(this.bagVolume);
      const formularyVolume = this.computeVolume(formulary.volume)
      return formularyVolume > maxVolume;
    },
    doseFactor(dosing) {
      let factor = 1;
      if (dosing) {
        if (dosing.startsWith('G')) {
          factor *= 1000;
        } else if (dosing.startsWith('MCG')) {
          factor /= 1000;
        } else if (dosing.startsWith('MUNITS')) {
          factor /= 1000;
        }
      }
      return factor;
    },
    weightFactor(weight, dosing) {
      if (dosing && dosing.includes('_KG_')) {
        return weight;
      }
      return 1;
    },
    timeFactor(dosing) {
      if (dosing && dosing.endsWith('_MIN')) {
        return 60;
      }
      return 1; // must be _HR
    },
    concentrationFactor(conc) {
      let factor = conc.denominatorValue / conc.numeratorValue;
      if (conc.numeratorUnits === 'WEIGHT_G') {
        factor /= 1000;
      } else if (conc.numeratorUnits === 'WEIGHT_MCG') {
        factor *= 1000;
      } else if (conc.numeratorUnits === 'WEIGHT_NG') {
        factor *= 1000000;
      } else if (conc.numeratorUnits === 'MUNITS') {
        factor *= 1000;
      }
      return factor;
    },
    computeRate(weight, formulary) {
      const dF = this.doseFactor(formulary.dosing);
      const wF = this.weightFactor(weight, formulary.dosing);
      const tF = this.timeFactor(formulary.dosing);
      const cF = this.concentrationFactor(this.concentration);
      const rate = formulary.dose * dF * wF * tF * cF;
      return rate.toFixed(3)
    },
    computeTime(vtbi, rate) {
      const minutes = (vtbi / rate) * 60;
      return [
        Math.floor(minutes / 60),
        Math.round(minutes % 60)
      ];
    },
    autoFillVolume(update, formulary) {
      if (!formulary || !formulary.volume) {
        return;
      }
      update.vtbi = this.computeVolume(formulary.volume);
    },
    autoFillRateAndTime(update, weight, formulary) {
      if (!formulary || (this.needWeight && !weight)) {
        return;
      }
      update.rate = this.computeRate(weight, formulary);
      [ update.hours, update.minutes ] = this.computeTime(
        update.vtbi ? update.vtbi : this.pumpSettings.vtbi,
        update.rate
      );
    },
    applyLimits(update) {
      const updatedSettings = assign({}, this.pumpSettings, update);

      // Limit VTBI to bag size
      if (updatedSettings.vtbi) {
        const volume = infusionBagVolumes.find(ibv => ibv.key === this.bagVolume).volumeML;
        update.vtbi = Math.min(updatedSettings.vtbi, volume, 1000);
      }
      if (updatedSettings.rate === Infinity) {
        update.rate = null;
      }
      if (updatedSettings.hours === Infinity) {
        update.hours = null;
      }
      if (updatedSettings.minutes === Infinity) {
        update.minutes = null;
      }
    },
    computeVolumeRateTime(update, field) {
      const updatedSettings = assign({}, this.pumpSettings, update);

      if (field === 'vtbi') {
        if (updatedSettings.rate) {
          const minutes = nullIfInfinity((updatedSettings.vtbi / updatedSettings.rate) * 60);
          update.hours = Math.floor(minutes / 60)
          update.minutes = Math.round(minutes % 60);
        } else if (updatedSettings.hours || updatedSettings.minutes) {
          const minutes = (updatedSettings.hours * 60) + updatedSettings.minutes;
          update.rate = nullIfInfinity(updatedSettings.vtbi / (minutes / 60));
        }
      }

      if (field === 'rate') {
        if (updatedSettings.vtbi) {
          const minutes = nullIfInfinity((updatedSettings.vtbi / updatedSettings.rate) * 60);
          update.hours = Math.floor(minutes / 60);
          update.minutes = Math.round(minutes % 60);
        } else if (updatedSettings.hours || updatedSettings.minutes) {
          const minutes = (updatedSettings.hours * 60) + updatedSettings.minutes;
          update.vtbi = nullIfInfinity(updatedSettings.rate * (minutes / 60));
        }
      }

      if (field === 'hours' || field === 'minutes') {
        const minutes = updatedSettings.hours * 60 + updatedSettings.minutes;
        if (updatedSettings.vtbi) {
          update.rate = nullIfInfinity(updatedSettings.vtbi / (minutes / 60));
          if (update.rate) {
            update.rate = update.rate.toFixed(2);
          }
        } else if (updatedSettings.rate) {
          update.vtbi = nullIfInfinity((minutes / 60) * updatedSettings.rate);
          if (update.vtbi) {
            update.vtbi = update.vtbi.toFixed(2);
          }
        }
      }
    },
    updateField(field, value) {
      if (this.isUpdating) {
        return;
      }
      this.isUpdating = true;

      let update = {
        ...this.pumpSettings,
        [field]: value
      }

      if (field === 'pumpFunction' && this.pumpSettings.pumpFunction !== value) {
        // Clear values when this changes
        update.weight = null;
        update.formulary = null;
        update.vtbi = null;
        update.hours = null;
        update.minutes = null;
        update.rate = null;
      }

      if (field === 'weight') {
        this.autoFillRateAndTime(update, value, this.pumpSettings.formulary);
      }
      if (field === 'formulary') {
        this.autoFillVolume(update, value);
        this.autoFillRateAndTime(update, this.pumpSettings.weight, value);
      }

      this.applyLimits(update)

      this.computeVolumeRateTime(update, field);

      this.$emit('update', update);

      this.$nextTick(() => {
        this.isUpdating = false;
      });
    },
  }
}
</script>

<style scoped>
.ivpump-options-content {
  flex: 1;
}

.volume-rate-time-options {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 16px;
  margin-top: 16px;
}

.formulary-options {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 16px;
  margin-top: 16px;
}

.formulary-form-container {
  display: flex;
  flex-direction: row;
  align-items: end;
}

.md-field.md-theme-default .md-suffix {
  padding: 0 10px;
  border: solid 1px #d2d2d2;
  background-color: #f3f3f3;
  color: #2b2b2b;
  border-radius: 5px;
}

</style>